import LabelledSwitch from '../LabelledSwitch';
import Avatar from '@material-ui/core/Avatar';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import DeleteIcon from '@material-ui/icons/Delete';
import SyncIcon from '@material-ui/icons/Sync';
import PropTypes from 'prop-types';
import React from 'react';
import AreYouSure from '../AreYouSure';
import BasicReadField from '../BasicReadField';
import MenuSelector from '../MenuSelector';
import PrimaryButton from '../PrimaryButton';
import { globalStyles as styles } from '../style/gstheme';
import TitledPanel from '../TitledPanel';
import TitledTextField from '../TitledTextField';
import { MAX_LONGIRI_LENGTH } from '../utils/constants';
import { checkIRI } from '../utils/utils';
import IntegrationActionViewer from './IntegrationActionViewer';

const menuOptions = [
    {
        "value": "application/ld+json",
        "label": "JSON-LD"
    },
    {
        "value": "text/turtle",
        "label": "Turtle"
    }
];

class IntegrationDetail extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isValidIRI: true,
            isDirty: false,
            changesOnly: props.data.changesOnly,
            originalChangesOnly: props.data.changesOnly,
            originalFormat: props.data.format,
            format: props.data.format,
            originalIntegrationEndpoint: props.data.integrationEndpoint || "",
            integrationEndpoint: props.data.integrationEndpoint || "",
            areYouSure: false
        };
    }

    checkIfSure = () => this.setState({ areYouSure: true });

    onSure = () => {
        this.setState({ areYouSure: false });
        this.props.onDeleteIntegration(this.props.data.id);
    };

    onNotSure = () => this.setState({ areYouSure: false });

    handleFormatChange = (format) => {
        this.setState({ format }, this.checkIfDirty);
    };

    checkIfDirty = () => {
        let isDirty = false;
        if (this.state.format !== this.state.originalFormat
            || this.state.changesOnly !== this.state.originalChangesOnly
            || this.state.integrationEndpoint !== this.state.originalIntegrationEndpoint) isDirty = true;
        this.setState({ isDirty });
    };

    handleIRIChange = event => {
        const t = event.target;
        const isValidIRI = checkIRI(t.value, true);
        this.setState({ integrationEndpoint: t.value, isValidIRI }, this.checkIfDirty);
    };

    handlePayloadChange = checked => this.setState({ changesOnly: checked }, this.checkIfDirty);

    saveUpdates = () => {
        this.props.onUpdateIntegration(this.props.data.id, this.state.integrationEndpoint, this.state.format, this.state.changesOnly);
        this.setState({
            isDirty: false,
            originalFormat: this.state.format,
            originalChangesOnly: this.state.changesOnly,
            originalIntegrationEndpoint: this.state.integrationEndpoint
        });
    };

    render() {

        const { data, classes, users } = this.props;
        const { areYouSure, format, isValidIRI, integrationEndpoint, isDirty, changesOnly } = this.state;
        const isReadOnly = false;

        return (
            <form autoComplete="off">
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '13px' }}>
                    <div style={{ display: 'flex' }}>
                        <Avatar className={classes.primaryAvatar}>
                            <SyncIcon />
                        </Avatar>
                        <Typography data-test="integration-name" style={{ marginLeft: '10px', marginTop: '3px' }} className={classes.mainHeading}>{data.label["en"]}</Typography>
                    </div>
                    {!isReadOnly &&
                        <Tooltip title='Remove integration' style={{ alignSelf: 'flex-end', marginTop: '5px', marginRight: 20 }}>
                            <IconButton aria-label="Remove integration" data-test="remove-integration" size="small" onClick={this.checkIfSure} className={classes.deleteButton}>
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    }
                </div>
                <Grid data-test="integration-details" container spacing={2} style={{ marginBottom: 5 }}>
                    <Grid item xs={12}>
                        <TitledTextField
                            margin
                            ariaLabel="Integration endpoint"
                            dataTest="integration-endpoint"
                            name="integrationEndpoint"
                            title="Endpoint"
                            error={!isValidIRI}
                            required={true}
                            placeholder="Enter endpoint to send data to"
                            errorText={!isValidIRI ? "Endpoint is not a valid address." : ""}
                            text={integrationEndpoint}
                            maxLength={MAX_LONGIRI_LENGTH}
                            onChange={this.handleIRIChange} />
                    </Grid>
                    <Grid item xs={12}>
                        <TitledPanel title="Public Key">
                            <BasicReadField ariaLabel="Public key" fontFamily="courier" rows={15} multiline dataTest="public-key" fullWidth text={data.publicKey} locked />
                        </TitledPanel>
                    </Grid>
                    <Grid item xs={4}>
                        <TitledPanel height="full" title="Format">
                            <div style={{ marginTop: 10 }}>
                                <MenuSelector
                                    dataTest="format-selector"
                                    handleChange={this.handleFormatChange}
                                    selected={format}
                                    hintWord="format"
                                    options={menuOptions} />
                            </div>
                        </TitledPanel>
                    </Grid>
                    <Grid item xs={4}>
                        <TitledPanel height="full" title="Payload">
                            <LabelledSwitch
                                dataTest="changesOnly"
                                disabled={isReadOnly}
                                checked={changesOnly}
                                handleChange={this.handlePayloadChange}
                                label="Changes Only" />
                        </TitledPanel>
                    </Grid>
                    <Grid item xs={12}>
                        <div style={{ float: 'right' }}>
                            <PrimaryButton dataTest="save" disabled={!isDirty || !isValidIRI} onClick={this.saveUpdates}>Save</PrimaryButton>
                        </div>
                    </Grid>
                </Grid>
                <IntegrationActionViewer integrationIRI={data.id} users={users} />
                {areYouSure && <AreYouSure onSure={this.onSure} onNotSure={this.onNotSure} message="Do you really want to remove this integration?" />}
            </form >
        );
    }
}

IntegrationDetail.propTypes = {
    classes: PropTypes.object,
    onUpdateIntegration: PropTypes.func.isRequired,
    onDeleteIntegration: PropTypes.func.isRequired,
    users: PropTypes.array,
    data: PropTypes.object.isRequired
};

export default withStyles(styles)(IntegrationDetail);